<template>
  <div>
    <v-row class="align-center">
      <v-col cols="12" class="d-flex align-center flex-wrap">
        <gg-card-title class="pt-0 mr-auto">{{ firstApp ? 'Deploy your first app' : 'Deploy an app' }}</gg-card-title>
        <!-- No apps yet: Applications sends people here, so "back" would loop. -->
        <gg-btn v-if="!firstApp" tile outlined color="primary" :to="{ name: 'applications' }">Back to applications</gg-btn>
      </v-col>
    </v-row>
    <p v-if="firstApp && step === 'app'" class="apps__muted mb-3">
      Install apps like WordPress, Uptime Kuma or Gitea in a few clicks — on one of your servers, or on a new one we set up for it.
    </p>

    <gg-card>
      <gg-card-text class="pa-0">
        <v-stepper :value="stepIndex" flat class="apps-deploy__stepper">
          <v-stepper-header>
            <template v-for="(s, i) in steps">
              <v-divider v-if="i" :key="'div-' + s.key" />
              <v-stepper-step :key="s.key" :step="i + 1" :complete="stepIndex > i + 1">{{ s.label }}</v-stepper-step>
            </template>
          </v-stepper-header>
        </v-stepper>

        <div class="pa-4">
          <!-- ── 1. The app and its questions (no server needed yet) ────── -->
          <template v-if="step === 'app'">
            <h3 class="body-1 font-weight-medium mb-1">Choose the app</h3>
            <kvm-docker-templates @choose="onChoose" />
          </template>

          <!-- The chosen app, on every later step. -->
          <gg-card v-if="choice && step !== 'app'" outlined class="mb-4">
            <gg-card-text class="pa-3 d-flex align-center">
              <kvm-logo :item="choice.template" :size="32" default-icon="mdi-docker" class="mr-3" />
              <div class="mr-auto">
                <div class="body-2 font-weight-medium">{{ appTitle }}</div>
                <div class="apps__muted text-caption">{{ choiceSummary }}</div>
              </div>
              <gg-btn v-if="canChange" small text class="apps__link" @click="changeApp">Change</gg-btn>
            </gg-card-text>
          </gg-card>

          <!-- ── 2. Where it runs ───────────────────────────────────────── -->
          <template v-if="step === 'where'">
            <div v-if="loadingServers" class="py-8 text-center" role="status">
              <v-progress-circular indeterminate color="primary" size="32" width="3" aria-hidden="true" />
              <div class="apps__muted mt-2">Finding your Docker servers…</div>
            </div>
            <template v-else>
              <h3 class="body-1 font-weight-medium mb-1">Where should it run?</h3>
              <p class="apps__muted mb-3">
                {{ servers.length ? 'Install it on one of your Docker servers, or on a new one.' : 'You don’t have a Docker server yet — we’ll set one up for it.' }}
              </p>
              <v-radio-group v-model="target" class="mt-0" hide-details aria-label="Where the app runs">
                <gg-card v-for="s in servers" :key="s.id" outlined class="apps-deploy__option mb-2"
                         :class="{ 'apps-deploy__option--on': target === s.id }">
                  <gg-card-text class="pa-3">
                    <v-radio :value="s.id" :disabled="!!unavailable(s)">
                      <template #label>
                        <div>
                          <div class="body-2 font-weight-medium">{{ s.name }}</div>
                          <div class="apps__muted text-caption">{{ unavailable(s) || details(s) }}</div>
                        </div>
                      </template>
                    </v-radio>
                    <!-- How much room it has: CPU load, free memory, free disk. -->
                    <kvm-server-resources v-if="!unavailable(s)" :server="s" class="apps-deploy__res" />
                  </gg-card-text>
                </gg-card>
                <gg-card outlined class="apps-deploy__option" :class="{ 'apps-deploy__option--on': target === 'new' }">
                  <gg-card-text class="pa-3">
                    <v-radio value="new">
                      <template #label>
                        <div>
                          <div class="body-2 font-weight-medium">A new server</div>
                          <div class="apps__muted text-caption">
                            We build a server with Docker and install {{ appTitle }} on it for you. You choose the plan next.
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </gg-card-text>
                </gg-card>
              </v-radio-group>
              <div class="mt-4">
                <gg-btn tile color="primary" class="apps__btn" :disabled="!target" @click="goToServerStep">Continue</gg-btn>
              </div>
            </template>
          </template>

          <!-- ── 3a. Install on one of their servers ────────────────────── -->
          <template v-else-if="step === 'install'">
            <div v-if="install.loading || install.submitting" class="py-8 text-center" role="status">
              <v-progress-circular indeterminate color="primary" size="32" width="3" aria-hidden="true" />
              <div class="apps__muted mt-2">
                {{ install.submitting ? `Installing ${appTitle} on ${targetServer.name}…` : `Checking ${targetServer ? targetServer.name : 'the server'}…` }}
              </div>
            </div>

            <!-- Installed: progress follows at the top; the details to keep. -->
            <template v-else-if="install.result">
              <applications-operations :service-id="target" />
              <v-alert type="success" dense text class="apps__alert mb-3">
                Installing {{ appTitle }} on {{ targetServer.name }} — you can follow its progress above.
              </v-alert>
              <p v-for="n in install.notes" :key="n" class="apps__muted text-caption mb-2">{{ n }}</p>
              <p v-if="installUrl" class="mb-2">
                It will be at <a :href="installUrl" target="_blank" rel="noopener" class="apps__link">{{ installUrl }}</a>
                <span class="apps__muted text-caption"> once it has started (HTTPS is set up on the first visit).</span>
              </p>
              <div v-if="generatedList.length" class="mb-3">
                <div class="subtitle-2 mb-1">Sign-in details — also kept on the app's Manage page</div>
                <div v-for="g in generatedList" :key="g.key" class="d-flex align-center mb-1">
                  <span class="apps__muted text-caption mr-2" style="min-width: 140px;">{{ g.label }}</span>
                  <kvm-copyable :value="g.value" :display="g.value" />
                </div>
              </div>
              <div v-if="postDeployHtml" class="apps-deploy__md mb-3" v-html="postDeployHtml" />
              <div class="d-flex flex-wrap">
                <gg-btn tile color="primary" class="apps__btn mr-2 mb-2"
                        :to="{ name: 'applications_app', params: { serviceId: target, app: install.stack } }">Manage the app</gg-btn>
                <gg-btn v-if="choice.template.device_pairing" tile outlined color="primary" class="mr-2 mb-2" @click="pairingOpen = true">
                  <v-icon left small aria-hidden="true">mdi-check-decagram</v-icon>Approve my browser
                </gg-btn>
                <gg-btn text class="mb-2" :to="{ name: 'applications' }">Go to Applications</gg-btn>
              </div>
              <kvm-docker-pairing v-if="choice.template.device_pairing" v-model="pairingOpen" :service-id="target"
                                  :stack="install.stack" :app-name="appTitle" />
            </template>

            <!-- It didn't go through: why, and what it was trying. -->
            <template v-else>
              <h3 class="body-1 font-weight-medium mb-2">Couldn't install on {{ targetServer.name }}</h3>
              <v-alert type="warning" dense text class="apps__alert mb-3" role="alert">{{ install.error }}</v-alert>
              <div class="apps-deploy__facts mb-3">
                <div><span class="apps__muted">App name</span><strong>{{ install.stack }}</strong></div>
                <div v-if="install.hostname"><span class="apps__muted">Address</span><strong>https://{{ install.hostname }}</strong></div>
              </div>
              <p v-for="n in install.notes" :key="n" class="apps__muted text-caption mb-1">{{ n }}</p>
              <div class="mt-4">
                <gg-btn text class="mr-2" @click="step = 'where'">Back</gg-btn>
                <gg-btn tile color="primary" class="apps__btn" @click="doInstall">Try again</gg-btn>
              </div>
            </template>
          </template>

          <!-- ── 3b. Plan & payment for a new server ────────────────────── -->
          <template v-else-if="step === 'order'">
            <gg-btn small text class="apps__link ml-n2 mb-2" @click="step = 'where'">
              <v-icon left small aria-hidden="true">mdi-arrow-left</v-icon>Back
            </gg-btn>
            <h3 class="body-1 font-weight-medium mb-1">Your new server</h3>
            <p class="apps__muted text-caption mb-0">It's built with Docker, and {{ appTitle }} is installed on it as soon as it's ready.</p>
            <hosting-kvm-order embedded @ordered="onOrdered" />
          </template>

          <!-- ── 4. Setting up the new server + its app ─────────────────── -->
          <template v-else-if="step === 'setup'">
            <div aria-live="polite">
              <v-alert v-if="setup.error" type="error" border="left" class="apps__alert-strong" role="alert">
                {{ setup.error }}
                <div class="mt-2">
                  <gg-btn v-if="newServiceId" small tile color="white" class="error--text" :to="serverRoute">Open the server</gg-btn>
                </div>
              </v-alert>

              <template v-else-if="setupDone">
                <v-alert v-if="appInstalled" type="success" border="left" class="apps__alert-strong">
                  {{ appTitle }} is ready on your new server.
                </v-alert>
                <v-alert v-else type="warning" border="left" class="apps__alert-strong">
                  Your server is ready, but {{ appTitle }} wasn't installed: {{ appProblem }}
                  You can deploy it from Applications.
                </v-alert>

                <template v-if="appInstalled">
                  <p v-if="installState && installState.url" class="mb-2">
                    Open it at <a :href="installState.url" target="_blank" rel="noopener" class="apps__link">{{ installState.url }}</a>
                    <span class="apps__muted text-caption"> (HTTPS is set up on the first visit)</span>
                  </p>
                  <p v-if="installState && installState.note" class="apps__muted mb-2">{{ installState.note }}</p>
                  <p v-if="setup.dnsNote" class="apps__muted mb-2">{{ setup.dnsNote }}</p>
                  <div v-if="appDetails && appDetails.credentials.length" class="mb-3">
                    <div class="subtitle-2 mb-1">Sign-in details — also on the app's Manage page</div>
                    <div v-for="c in appDetails.credentials" :key="c.label" class="d-flex align-center mb-1">
                      <span class="apps__muted text-caption mr-2" style="min-width: 140px;">{{ c.label }}</span>
                      <kvm-copyable :value="c.value" :display="c.value" />
                    </div>
                  </div>
                </template>

                <div class="d-flex flex-wrap mt-3">
                  <gg-btn v-if="appInstalled" tile color="primary" class="apps__btn mr-2 mb-2"
                          :to="{ name: 'applications_app', params: { serviceId: newServiceId, app: choice.stack } }">Manage the app</gg-btn>
                  <gg-btn tile outlined color="primary" class="mr-2 mb-2" :to="serverRoute">Open the server</gg-btn>
                  <gg-btn text class="mb-2" :to="{ name: 'applications' }">Go to Applications</gg-btn>
                </div>
              </template>

              <template v-else>
                <ol class="apps-deploy__progress mb-3">
                  <li v-for="p in phases" :key="p.label" :class="'is-' + p.state">
                    <v-progress-circular v-if="p.state === 'active'" indeterminate size="18" width="2" color="primary" class="mr-2" aria-hidden="true" />
                    <v-icon v-else small :color="p.state === 'done' ? 'success' : 'grey'" class="mr-2" aria-hidden="true">
                      {{ p.state === 'done' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    {{ p.label }}
                  </li>
                </ol>
                <p class="apps__muted mb-0">
                  This usually takes a few minutes. You can leave this page — {{ appTitle }} is installed either way,
                  and you'll find it under Applications.
                </p>
              </template>
            </div>
          </template>
        </div>
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import kvm from '@/api/kvm'
import { renderMarkdown } from '@/helpers/markdown'
import { addDockerOperation } from '@/helpers/dockerOperations'
import { kvmServices, dockerServerInfo, dockerServerUnavailable } from '@/helpers/dockerServers'
import { dnsRoute, currentRecords, conflictsWith, pointRecord } from '@/helpers/ownDomains'
import ApplicationsOperations from '@/views/applications/ApplicationsOperations'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'
import KvmDockerPairing from '@/views/services/hosting/kvm/KvmDockerPairing'
import KvmDockerTemplates from '@/views/services/hosting/kvm/KvmDockerTemplates'
import HostingKvmOrder from '@/views/services/hosting/kvm/HostingKvmOrder'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'
import KvmServerResources from '@/views/services/hosting/kvm/KvmServerResources'

const SETUP_POLL_MS = 5000
// Stop following after an hour (the server keeps going; it's on Applications).
const SETUP_MAX_POLLS = 720

// wordpress → wordpress-2 → wordpress-3 … (first name not already taken).
const uniqueName = (base, taken) => {
  if (!taken.includes(base)) return base
  let n = 2
  while (taken.includes(`${base}-${n}`)) n++
  return `${base}-${n}`
}
const subdomainFor = name => String(name || '').toLowerCase().replace(/_/g, '-')
  .replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '').slice(0, 63).replace(/-+$/, '')

/**
 * "Deploy new app" from Applications. First the app and its questions (the
 * shared catalog, with no server yet), then where it runs: one of the customer's
 * Docker servers (confirm → installed there, renamed if the name's taken) or a
 * new server (the Add a Server form, then the server is built as a Docker host
 * with the app — POST /provision docker_app — and this page follows it until the
 * app is up). `?server=<id>` ("Deploy app here") skips the server question.
 */
export default {
  name: 'ApplicationsDeploy',
  components: { ApplicationsOperations, KvmCopyable, KvmDockerPairing, KvmDockerTemplates, HostingKvmOrder, KvmLogo, KvmServerResources },
  data: () => ({
    step: 'app',
    loadingServers: true,
    servers: [],
    target: null, // a server id, or 'new'
    // The app: { template, stack, variables, publish, subdomain, subdomainAuto, path } or
    // a custom one: { custom: true, template: {name…}, stack, compose }.
    choice: null,
    // Installing on an existing server: what it will be called / where, then the result.
    install: { loading: false, submitting: false, error: '', stack: '', hostname: null, notes: [], result: null },
    pairingOpen: false,
    newServiceId: null,
    setup: { error: '', server: null, appProblem: '', dnsNote: null, polls: 0 },
    appDetails: null,
    pollTimer: null,
  }),
  computed: {
    ...mapGetters(['services', 'servicesPopulated']),
    steps () {
      const steps = [{ key: 'app', label: 'Choose the app' }, { key: 'where', label: 'Where it runs' }]
      if (this.target === 'new') steps.push({ key: 'order', label: 'Plan & payment' }, { key: 'setup', label: 'Setting up' })
      else steps.push({ key: 'install', label: 'Install' })
      return steps
    },
    stepIndex () {
      return this.steps.findIndex(s => s.key === this.step) + 1
    },
    kvmIds () {
      // Wait for the services list: before it loads there'd seem to be no servers.
      return this.servicesPopulated ? kvmServices(this.services).map(s => String(s.id)).join(',') : null
    },
    // Sent here by Applications because there are no apps yet.
    firstApp () {
      return this.$route.query.first === '1'
    },
    presetServer () {
      return this.servers.find(s => s.id === String(this.$route.query.server || '') && !this.unavailable(s)) || null
    },
    targetServer () {
      return this.servers.find(s => s.id === this.target) || null
    },
    appTitle () {
      const c = this.choice
      if (!c) return 'the app'
      return c.custom ? c.stack : c.template.name
    },
    choiceSummary () {
      const c = this.choice
      if (c.custom) return 'Custom app from your docker-compose file'
      if (!c.publish) return 'Reached at its server’s IP address'
      return `At ${c.addressPreview || c.subdomain + '.<its server’s free domain>'}, with HTTPS`
    },
    // Changing the app is fine until something has been installed or paid for.
    canChange () {
      return ['where', 'install', 'order'].includes(this.step) && !this.install.result && !this.install.loading && !this.install.submitting
    },
    generatedList () {
      const gen = (this.install.result && this.install.result.generated) || {}
      const vars = this.choice.template.variables || []
      return Object.keys(gen).map(key => ({ key, value: gen[key], label: (vars.find(v => v.key === key) || {}).label || key }))
    },
    postDeployHtml () {
      return renderMarkdown(this.install.result && this.install.result.post_deploy)
    },
    installUrl () {
      const r = this.install.result && this.install.result.route
      return r ? 'https://' + r.hostname + (r.path && r.path !== '/' ? r.path + '/' : '/') : ''
    },
    serverRoute () {
      return { name: 'hosting_kvm_info', params: { hostingid: this.newServiceId } }
    },
    installState () {
      return (this.setup.server && this.setup.server.docker_app_install) || null
    },
    appInstalled () {
      return !!(this.installState && this.installState.state === 'installed')
    },
    appProblem () {
      return this.setup.appProblem || (this.installState && this.installState.error) || 'something went wrong.'
    },
    // Terminal: the app is installed or failed — or, when it couldn't be queued
    // with the server, the server itself is ready.
    setupDone () {
      const s = this.setup.server
      if (!s) return false
      if (this.installState) return ['installed', 'failed'].includes(this.installState.state)
      return !!(s.install && ['ready', 'failed'].includes(s.install.state))
    },
    phases () {
      const s = this.setup.server
      const built = !!s && s.provisioning_state === 'provisioned' && s.vm_status !== 'provisioning'
      const ready = built && !!s.install && s.install.state === 'ready'
      return [
        { label: 'Payment received', state: 'done' },
        { label: 'Building your server', state: built ? 'done' : 'active' },
        { label: 'Installing Docker', state: ready ? 'done' : (built ? 'active' : 'todo') },
        { label: `Installing ${this.appTitle}`, state: this.appInstalled ? 'done' : (ready ? 'active' : 'todo') },
      ]
    },
  },
  watch: {
    kvmIds: { immediate: true, handler () { this.loadServers() } },
  },
  beforeDestroy () {
    clearTimeout(this.pollTimer)
  },
  methods: {
    unavailable: dockerServerUnavailable,
    details (s) {
      return [s.ip, s.location].filter(v => v && v !== '—').join(' · ')
    },
    async loadServers () {
      if (this.kvmIds === null) return
      const found = await Promise.all(kvmServices(this.services).map(svc => dockerServerInfo(svc).catch(() => null)))
      this.servers = found.filter(Boolean)
      this.loadingServers = false
      if (!this.target) {
        if (this.presetServer) this.target = this.presetServer.id
        else if (!this.servers.some(s => !this.unavailable(s))) this.target = 'new'
      }
    },
    onChoose (choice) {
      this.choice = choice
      // "Deploy app here": the server is already known.
      if (this.presetServer && this.target === this.presetServer.id) this.goToServerStep()
      else this.step = 'where'
    },
    changeApp () {
      this.install = { loading: false, submitting: false, error: '', stack: '', hostname: null, notes: [], result: null }
      this.step = 'app'
    },
    goToServerStep () {
      if (this.target === 'new') {
        this.step = 'order'
        return
      }
      this.step = 'install'
      this.prepareInstall()
    },
    // What the app will be called and where it'll be on this server — a name
    // that's free there, and its subdomain of the server's free domain — then
    // straight on to installing it (nothing left to ask).
    async prepareInstall () {
      const c = this.choice
      this.install = { loading: true, submitting: false, error: '', stack: c.stack, hostname: null, notes: [], result: null }
      const [stacks, proxy] = await Promise.all([
        kvm.dockerStacks(this.target).catch(() => []),
        c.custom || !c.publish ? Promise.resolve(null) : kvm.dockerProxy(this.target).catch(() => null),
      ])
      const stack = uniqueName(c.stack, stacks.map(s => s.name))
      const notes = []
      if (stack !== c.stack) notes.push(`This server already has an app called “${c.stack}”, so this one is “${stack}”.`)
      let hostname = null
      if (!c.custom && c.publish) {
        if (proxy && proxy.installed && c.ownDomain) {
          hostname = c.ownDomain.hostname
        } else if (proxy && proxy.installed && proxy.free_domain) {
          const sub = c.subdomainAuto ? subdomainFor(stack) : c.subdomain
          hostname = `${sub}.${proxy.free_domain}`
        } else {
          notes.push('This server’s web proxy isn’t set up, so the app is reached at the server’s IP address for now. You can add a domain under Domains later.')
        }
      }
      this.install = { ...this.install, loading: false, stack, hostname, notes, serverIp: proxy && proxy.server_ip }
      await this.doInstall()
    },
    // Their own domain: point it at the server (only replacing what's there if
    // they agreed to when choosing it). Returns a note for the customer, or null.
    async setUpOwnDomain (own, ip) {
      if (!ip) return `Point an A record for ${own.hostname} to your server’s IP address.`
      const route = await dnsRoute(own.entry)
      if (route.kind === 'manual') return `Point an A record for ${own.hostname} to ${ip} — HTTPS starts working once it does.`
      const conflicts = conflictsWith(await currentRecords(route, own.sub), ip)
      if (conflicts.length && !own.confirmed) {
        throw new Error(`${own.hostname} now points to ${conflicts.map(r => r.address).join(', ')} — go back and choose it again to confirm replacing that.`)
      }
      await pointRecord(route, own.sub, ip)
      return null
    },
    async doInstall () {
      const c = this.choice
      const i = this.install
      i.submitting = true
      i.error = ''
      try {
        if (c.ownDomain && i.hostname && !i.dnsDone) {
          const note = await this.setUpOwnDomain(c.ownDomain, i.serverIp)
          if (note) i.notes = [...i.notes, note]
          i.dnsDone = true
        }
        if (c.custom) {
          const op = await kvm.dockerSaveStack(this.target, i.stack, c.compose)
          i.result = { operation: op, generated: {}, post_deploy: null, route: null }
        } else {
          i.result = await kvm.dockerDeployTemplate(this.target, c.template.slug, i.stack, c.variables, i.hostname, c.path || '/')
        }
        addDockerOperation(this.target, i.result.operation)
      } catch (e) {
        i.error = e.data && e.data.errors ? Object.values(e.data.errors).flat().join(' ') : e.message
      } finally {
        i.submitting = false
      }
    },
    // Paid and created: build it as a Docker host with the app to install.
    async onOrdered (serviceId) {
      this.newServiceId = serviceId
      this.step = 'setup'
      this.setup = { error: '', server: null, appProblem: '', dnsNote: null, polls: 0 }
      try {
        const [service, catalog] = await Promise.all([kvm.getService(serviceId), kvm.getCatalog(serviceId)])
        const host = (catalog.apps || []).find(a => a.docker_manager)
        if (!host) throw new Error('Docker servers can’t be set up right now, so your new server is waiting for you to choose what to install on it.')
        const free = service.suggested_domain
        const c = this.choice
        let dockerApp
        if (c.custom) {
          dockerApp = { stack: c.stack, compose: c.compose }
        } else {
          dockerApp = { template: c.template.slug, stack: c.stack, variables: c.variables, path: c.path || '/' }
          if (c.publish && c.ownDomain) {
            dockerApp.hostname = c.ownDomain.hostname
            // The server's IP is reserved with the order: its DNS can be set now.
            try {
              this.setup.dnsNote = await this.setUpOwnDomain(c.ownDomain, service.dns && service.dns.target_ip)
            } catch (e) {
              this.setup.dnsNote = `Couldn't set up ${c.ownDomain.hostname}: ${e.message} Point an A record for it to ${(service.dns && service.dns.target_ip) || 'your server’s IP'} yourself.`
            }
          } else if (c.publish && free) {
            dockerApp.hostname = c.subdomain ? `${c.subdomain}.${free}` : free
          }
        }
        const build = { app: host.slug }
        if (free) build.domain = free
        try {
          await kvm.provision(serviceId, { ...build, docker_app: dockerApp })
        } catch (e) {
          if (e.status === 409) {
            // Already building (e.g. a second tab) — just follow it.
          } else if (e.status === 422) {
            // The app's settings were refused: build the server anyway, so the
            // order isn't left waiting; the app can be deployed on it after.
            this.setup.appProblem = e.message
            await kvm.provision(serviceId, build)
          } else {
            throw e
          }
        }
        this.pollSetup()
      } catch (e) {
        this.setup.error = `Your server was created, but setting it up didn't start: ${e.message}`
      }
    },
    async pollSetup () {
      clearTimeout(this.pollTimer)
      try {
        this.setup.server = await kvm.getServer(this.newServiceId)
      } catch (e) { /* transient — try again */ }
      if (this.setupDone) {
        if (this.appInstalled) this.loadAppDetails()
        return
      }
      if (++this.setup.polls < SETUP_MAX_POLLS) this.pollTimer = setTimeout(this.pollSetup, SETUP_POLL_MS)
    },
    // Its sign-in details (kept on the server, in the app's own folder).
    async loadAppDetails () {
      try {
        const st = await kvm.dockerStack(this.newServiceId, this.choice.stack)
        this.appDetails = st.app ? { ...st.app, credentials: st.app.credentials || [] } : null
      } catch (e) { /* the Manage page has them too */ }
    },
  },
}
</script>

<style scoped lang="scss">
@import '../services/hosting/kvm/kvm-ui';

.apps__muted { @include kvm-secondary-text; }
.apps__link { @include kvm-text-green-aa; }
.apps__btn {
  @include kvm-btn-primary-aa;
  &:focus-visible { @include kvm-focus-ring; }
}
.apps-deploy__option--on { border-color: $kvm-focus-light !important; }
// Lined up under the server's name (past the radio button).
.apps-deploy__res { margin-left: 32px; }
.apps-deploy__facts > div {
  display: flex;
  gap: 12px;
  padding: 2px 0;
  span { min-width: 90px; }
}
.apps-deploy__md ::v-deep p:last-child { margin-bottom: 0; }
.apps-deploy__progress {
  list-style: none;
  padding-left: 0;
  li { display: flex; align-items: center; padding: 4px 0; }
  .is-todo { @include kvm-secondary-text; }
  .is-active { font-weight: 500; }
}
// `text` alerts: near-black / near-white body text for AA, as on the order page.
.apps__alert ::v-deep .v-alert__content { color: #212121 !important; }
.theme--dark .apps__alert ::v-deep .v-alert__content { color: #eceff1 !important; }
// Prominent alerts: black body text on the solid fill (AA), as on the order page.
.apps__alert-strong ::v-deep .v-alert__content { color: #000 !important; }
::v-deep .v-stepper__header { flex-wrap: wrap; height: auto; box-shadow: none; }
::v-deep .v-stepper__label { @include kvm-secondary-text; }
::v-deep .v-stepper__step--active .v-stepper__label,
::v-deep .v-stepper__step--complete .v-stepper__label { color: inherit; }
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
@media (max-width: $kvm-bp-mobile) {
  ::v-deep .v-stepper__header .v-divider { display: none; }
  ::v-deep .v-stepper__label { display: flex !important; }
}
</style>
